import { useForm } from 'antd/lib/form/Form';
import Form from 'antd/lib/form';
import DatePicker from 'antd/lib/date-picker';
import dayjs from 'dayjs';
import { useSidebarColors } from '../../styles/themeMode';
import React, { useEffect, useState } from 'react';
import { OrgMetricsDateRange } from './OrgNavigationDateRange';

export const MAX_ORG_METRIC_PERIOD_IN_DAYS = 365;
export const MIN_ORG_METRIC_PERIOD_IN_DAYS = 90;

interface MetricsDateRange {
    metricsDateRange: dayjs.Dayjs[];
}

interface OrgOverviewDatePickerTimeRangeProps {
    currentDateRange: OrgMetricsDateRange;
    onFinish: (range: MetricsDateRange) => void;
    isLoading: boolean;
}

export const OrgOverviewDatePickerTimeRange = ({
    currentDateRange,
    onFinish,
    isLoading,
}: OrgOverviewDatePickerTimeRangeProps) => {
    const defaultDates = [dayjs().subtract(MIN_ORG_METRIC_PERIOD_IN_DAYS, 'days'), dayjs()];

    const [form] = useForm();
    const [userUpdatedCalendarDates, setUserUpdatedCalendarDates] = useState(false);

    const sidebarColors = useSidebarColors();

    const disabledDate = (current) => {
        if (!current) {
            return false;
        }
        const latestAllowedDate = dayjs();
        const earliestAllowedDate = dayjs().subtract(MAX_ORG_METRIC_PERIOD_IN_DAYS, 'day').startOf('day');
        return current.isBefore(earliestAllowedDate) || current.isAfter(latestAllowedDate);
    };

    const onRangePickerChanges = (changedFields) => {
        const changedField = changedFields.find((field) => field.name.includes('metricsDateRange'));
        if (!changedField) return;

        const changedValue = changedField.value || [];
        const [start, end] = changedValue;

        const isCleared = !start && !end;
        const isDefault = dayjs(defaultDates[0]).isSame(start, 'day') && dayjs(defaultDates[1]).isSame(end, 'day');

        if (isCleared) {
            form.setFieldsValue({ metricsDateRange: defaultDates });
            onFinish({ metricsDateRange: defaultDates });
            setUserUpdatedCalendarDates(false); // this disables the button
        } else if (!isDefault) {
            // it's truly a user change
            setUserUpdatedCalendarDates(true);
        } else {
            // they changed to defaultDates manually
            setUserUpdatedCalendarDates(false);
        }
    };

    const watchedRange = Form.useWatch('metricsDateRange', form);

    useEffect(() => {
        const hasUserTouched = userUpdatedCalendarDates;

        const isValidRange = () => {
            if (!watchedRange || watchedRange.length !== 2) return false;
            const [start, end] = watchedRange;
            if (!dayjs.isDayjs(start) || !dayjs.isDayjs(end)) return false;
            if (start.isAfter(dayjs()) || end.isAfter(dayjs())) return false;

            const dayDiff = end.diff(start, 'day');
            return dayDiff <= MAX_ORG_METRIC_PERIOD_IN_DAYS;
        };

        if (hasUserTouched && isValidRange() && !isLoading) {
            form.submit();
        }
    }, [watchedRange, userUpdatedCalendarDates, isLoading]);

    useEffect(() => {
        if (currentDateRange?.start && currentDateRange?.end) {
            form.setFieldsValue({ metricsDateRange: [dayjs(currentDateRange.start), dayjs(currentDateRange.end)] });
        }
    }, [currentDateRange, form]);

    return (
        <Form
            form={form}
            onFinish={(dates) => {
                onFinish(dates);
                setUserUpdatedCalendarDates(false);
            }}
            layout="vertical"
            initialValues={{ metricsDateRange: defaultDates }}
            style={{
                display: 'inline-flex',
                justifyContent: 'end',
                alignItems: 'center',
                height: 'fit-content',
            }}
            onFieldsChange={onRangePickerChanges}
        >
            <div style={{ maxHeight: '40px', display: 'flex', overflow: 'visible' }}>
                <Form.Item
                    name="metricsDateRange"
                    style={{
                        justifyContent: 'end',
                        font: 'inherit',
                    }}
                    rules={[
                        {
                            validator: (_, value) => {
                                if (!value || value.length !== 2) {
                                    return Promise.reject(new Error('Please select a valid date range'));
                                }

                                const [start, end] = value;

                                if (!dayjs.isDayjs(start) || !dayjs.isDayjs(end)) {
                                    return Promise.reject(new Error('Invalid date format'));
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <DatePicker.RangePicker
                        disabledDate={disabledDate}
                        style={{
                            border: `1px solid ${sidebarColors.border}`,
                            borderRadius: `0 0 4px 4px`,
                        }}
                    />
                </Form.Item>
            </div>
        </Form>
    );
};
